<template>
    <article class="album-card" @click.stop>
        <div v-if="isVisualCard && visualImages.length" :key="gridRenderKey" class="card-grid" :class="gridClass">
            <button
                v-for="(image, imageIndex) in visualImages"
                :key="`${entry.id}-${imageIndex}-${image}`"
                type="button"
                class="photo-button"
                :class="{ 'video-button': isVideo && imageIndex === 0 }"
                @click="isVideo && imageIndex === 0 ? handleVideoTap() : $emit('preview', entry, imageIndex)"
            >
                <video
                    v-if="shouldRenderVideo(imageIndex)"
                    ref="videoEl"
                    class="video-thumb"
                    :src="shouldLoadVideo ? playableVideoUrl : ''"
                    :poster="videoPoster"
                    muted
                    autoplay
                    loop
                    playsinline
                    webkit-playsinline
                    preload="metadata"
                    @loadedmetadata="onVideoReady"
                    @loadeddata="playVisibleVideo"
                    @canplay="playVisibleVideo"
                    @playing="revealPlayingVideo"
                    @timeupdate="revealPlayingVideo"
                    @error="handleVideoError"
                ></video>
                <img
                    v-if="!isVideo || imageIndex !== 0"
                    class="album-image"
                    :class="{ loaded: isImageLoaded(imageIndex) }"
                    :src="image"
                    :alt="`${entry.caption || 'album-image'}-${imageIndex + 1}`"
                    :loading="priority ? 'eager' : 'lazy'"
                    :fetchpriority="priority ? 'high' : 'auto'"
                    decoding="async"
                    @load="markImageLoaded(imageIndex)"
                    @error="handleImageError($event, imageIndex)"
                >
                <img
                    v-if="isVideo && imageIndex === 0"
                    class="album-image video-poster-overlay loaded"
                    :class="{ hidden: !shouldShowVideoPosterOverlay }"
                    :src="videoThumbnail"
                    :alt="`${entry.caption || 'video-cover'}-${imageIndex + 1}`"
                    :loading="priority ? 'eager' : 'lazy'"
                    :fetchpriority="priority ? 'high' : 'auto'"
                    decoding="async"
                    @error="handleImageError($event, imageIndex)"
                >
                <span v-if="isVideo && imageIndex === 0" class="video-badge">
                    <span class="video-icon">▶</span>
                    {{ entry.videoDuration }}
                </span>
            </button>
        </div>

        <div class="card-meta">
            <div class="meta-main">
                <div class="meta-status">
                    <span class="type-tag">{{ typeLabel }}</span>
                    <span class="meta-author">{{ entry.author }}</span>
                    <span class="meta-time">{{ entry.relativeTime }}</span>
                    <span
                        class="status-tag"
                        :class="{ pending: entry.uploadStatus !== 'done' }"
                    >{{ entry.uploadText }}</span>
                </div>
                <div v-if="entry.caption" class="meta-caption">{{ entry.caption }}</div>
                <div class="meta-location">{{ entry.location }}</div>
                <div v-if="showUploadMeter" class="entry-upload-meter">
                    <div class="progress-track entry-progress-track">
                        <div class="progress-bar entry-progress-bar" :style="{ width: `${entryUploadPercent}%` }"></div>
                    </div>
                    <span class="entry-upload-text">{{ entry.uploadText }}</span>
                </div>
            </div>

            <div class="meta-actions">
                <button
                    type="button"
                    class="action-button more-button"
                    @touchend.stop.prevent="handleCardAction('toggle-menu', $event)"
                    @click.stop="handleCardAction('toggle-menu', $event)"
                >
                    ···
                </button>
            </div>
        </div>

        <div v-if="menuVisible" class="floating-menu">
            <div class="menu-row">
                <button
                    type="button"
                    class="menu-button"
                    @touchend.stop.prevent="handleCardAction('edit', $event)"
                    @click.stop="handleCardAction('edit', $event)"
                >编辑</button>
                <button
                    type="button"
                    class="menu-button danger-button"
                    @touchend.stop.prevent="handleCardAction('remove', $event)"
                    @click.stop="handleCardAction('remove', $event)"
                >删除</button>
            </div>
        </div>
    </article>
</template>

<script>
const VIDEO_PLACEHOLDER = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="720" height="720" viewBox="0 0 720 720"><rect width="720" height="720" rx="56" fill="#1A2B49"/><circle cx="360" cy="360" r="118" fill="rgba(255,255,255,0.18)"/><path d="M325 298 L454 360 L325 422 Z" fill="#FFFFFF"/></svg>')}`;

function isQuickTimeCompatibilityUrl (url) {
    return /\.quicktime(?:[?#]|$)/i.test(String(url || ''));
}

export default {
    name: 'BabyAlbumCard',
    props: {
        entry: {
            type: Object,
            required: true
        },
        menuVisible: {
            type: Boolean,
            default: false
        },
        priority: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            shouldLoadVideo: false,
            isVideoVisible: false,
            videoFailed: false,
            videoReadyToReveal: false,
            hasRequestedVideoLoad: false,
            videoBlobUrl: '',
            videoBlobSourceUrl: '',
            videoBlobLoading: false,
            loadedImageMap: {}
        };
    },
    mounted () {
        if (!this.isVideo || !this.entry.videoUrl || typeof IntersectionObserver === 'undefined') {
            this.shouldLoadVideo = this.isVideo && !!this.entry.videoUrl;
            this.isVideoVisible = this.shouldLoadVideo;
            this.$nextTick(this.loadAndPlayVisibleVideo);
            return;
        }

        if (this.priority) {
            this.shouldLoadVideo = true;
        }
        this._observer = new IntersectionObserver((entries) => {
            const currentEntry = entries[0];
            this.isVideoVisible = !!currentEntry?.isIntersecting;
            if (this.isVideoVisible) {
                this.shouldLoadVideo = true;
                this.$nextTick(this.loadAndPlayVisibleVideo);
                return;
            }

            this.pauseVideo();
        }, {
            rootMargin: '240px 0px',
            threshold: 0.12
        });
        this._observer.observe(this.$el);
        this.installVisibilityFallback();
    },
    beforeDestroy () {
        if (this._observer) {
            this._observer.disconnect();
            this._observer = null;
        }
        this.removeVisibilityFallback();
        this.pauseVideo();
        this.releaseVideoBlobUrl();
    },
    methods: {
        handleCardAction (action, event) {
            const eventType = event?.type || 'unknown';
            const now = Date.now();
            if (eventType === 'click' && this._lastTouchAction === action && now - this._lastTouchAt < 700) {
                return;
            }
            if (eventType === 'touchend') {
                this._lastTouchAction = action;
                this._lastTouchAt = now;
            }

            this.$emit('debug', {
                type: 'card_action',
                action,
                inputType: eventType,
                entryId: this.entry.id
            });

            if (action === 'toggle-menu') {
                this.$emit('toggle-menu', this.entry.id);
                return;
            }
            this.$emit(action, this.entry);
        },
        handleImageError (event, imageIndex) {
            if (!this.isVideo || imageIndex !== 0 || !event?.target) {
                return;
            }

            event.target.src = VIDEO_PLACEHOLDER;
            this.markImageLoaded(imageIndex);
        },
        markImageLoaded (imageIndex) {
            this.$set(this.loadedImageMap, imageIndex, true);
        },
        isImageLoaded (imageIndex) {
            return !!this.loadedImageMap[imageIndex];
        },
        shouldRenderVideo (imageIndex) {
            return this.isVideo && imageIndex === 0 && !!this.entry.videoUrl && !this.videoFailed;
        },
        getVideoEl () {
            return Array.isArray(this.$refs.videoEl) ? this.$refs.videoEl[0] : this.$refs.videoEl;
        },
        onVideoReady () {
            this.playVisibleVideo();
        },
        playVisibleVideo () {
            const video = this.getVideoEl();
            if (!video || !this.isVideoVisible) {
                return;
            }

            video.muted = true;
            video.defaultMuted = true;
            video.playsInline = true;
            video.setAttribute('muted', 'muted');
            video.setAttribute('playsinline', 'playsinline');
            video.setAttribute('webkit-playsinline', 'webkit-playsinline');
            video.play()
                .then(() => {
                    this.$emit('debug', {
                        type: 'list_video_playing',
                        entryId: this.entry.id
                    });
                })
                .catch(error => {
                    this.$emit('debug', {
                        type: 'list_video_play_failed',
                        entryId: this.entry.id,
                        message: error?.message || String(error || '')
                    });
                });
        },
        loadAndPlayVisibleVideo () {
            const video = this.getVideoEl();
            if (!video || !this.isVideoVisible) {
                return;
            }

            if (!this.hasRequestedVideoLoad) {
                this.hasRequestedVideoLoad = true;
                try {
                    video.load();
                } catch (error) {}
            }
            this.playVisibleVideo();
        },
        revealPlayingVideo () {
            const video = this.getVideoEl();
            if (!video || video.paused || video.readyState < 2) {
                return;
            }
            this.videoReadyToReveal = true;
        },
        checkVideoVisibility () {
            if (!this.isVideo || !this.entry.videoUrl || !this.$el) {
                return;
            }

            const rect = this.$el.getBoundingClientRect();
            const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
            const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
            const visibleRatio = visibleHeight / Math.max(1, rect.height || 1);
            this.isVideoVisible = visibleHeight > 80 && visibleRatio > 0.08;
            if (this.isVideoVisible) {
                this.shouldLoadVideo = true;
                this.$nextTick(this.loadAndPlayVisibleVideo);
                return;
            }
            this.pauseVideo();
        },
        requestVisibilityCheck () {
            if (this._visibilityRaf) {
                return;
            }
            this._visibilityRaf = window.requestAnimationFrame(() => {
                this._visibilityRaf = 0;
                this.checkVideoVisibility();
            });
        },
        installVisibilityFallback () {
            if (typeof window === 'undefined') {
                return;
            }

            this._visibilityHandler = () => this.requestVisibilityCheck();
            window.addEventListener('scroll', this._visibilityHandler, { passive: true });
            window.addEventListener('resize', this._visibilityHandler, { passive: true });
            document.addEventListener('visibilitychange', this._visibilityHandler);
            this.requestVisibilityCheck();
        },
        removeVisibilityFallback () {
            if (typeof window === 'undefined' || !this._visibilityHandler) {
                return;
            }

            window.removeEventListener('scroll', this._visibilityHandler);
            window.removeEventListener('resize', this._visibilityHandler);
            document.removeEventListener('visibilitychange', this._visibilityHandler);
            this._visibilityHandler = null;
            if (this._visibilityRaf) {
                window.cancelAnimationFrame(this._visibilityRaf);
                this._visibilityRaf = 0;
            }
        },
        pauseVideo () {
            const video = this.getVideoEl();
            if (video) {
                video.pause();
            }
        },
        releaseVideoBlobUrl () {
            if (this.videoBlobUrl && typeof URL !== 'undefined') {
                URL.revokeObjectURL(this.videoBlobUrl);
            }
            this.videoBlobUrl = '';
            this.videoBlobSourceUrl = '';
            this.videoBlobLoading = false;
        },
        async preparePlayableVideoUrl () {
            if (
                !this.isVideo
                || !this.entry.videoUrl
                || !isQuickTimeCompatibilityUrl(this.entry.videoUrl)
                || this.videoBlobSourceUrl === this.entry.videoUrl
                || this.videoBlobLoading
                || typeof fetch !== 'function'
                || typeof URL === 'undefined'
            ) {
                return;
            }

            const sourceUrl = this.entry.videoUrl;
            this.videoBlobLoading = true;
            try {
                const response = await fetch(sourceUrl, { mode: 'cors', cache: 'force-cache' });
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                const sourceBlob = await response.blob();
                if (!this.isVideo || this.entry.videoUrl !== sourceUrl) {
                    return;
                }
                const playableBlob = new Blob([sourceBlob], {
                    type: sourceBlob.type && sourceBlob.type !== 'application/octet-stream'
                        ? sourceBlob.type
                        : 'video/quicktime'
                });
                this.releaseVideoBlobUrl();
                this.videoBlobUrl = URL.createObjectURL(playableBlob);
                this.videoBlobSourceUrl = sourceUrl;
                this.$nextTick(this.loadAndPlayVisibleVideo);
            } catch (error) {
                this.$emit('debug', {
                    type: 'list_video_blob_failed',
                    entryId: this.entry.id,
                    message: error?.message || String(error || ''),
                    src: sourceUrl
                });
            } finally {
                this.videoBlobLoading = false;
            }
        },
        handleVideoError () {
            const video = this.getVideoEl();
            this.$emit('debug', {
                type: 'list_video_error',
                entryId: this.entry.id,
                code: video?.error?.code || 0,
                message: video?.error?.message || ''
            });
            if (isQuickTimeCompatibilityUrl(this.entry.videoUrl) && !this.videoBlobUrl) {
                this.preparePlayableVideoUrl();
                return;
            }
            this.videoFailed = true;
            this.videoReadyToReveal = false;
        },
        handleVideoTap () {
            this.$emit('preview', this.entry, 0);
        }
    },
    watch: {
        'entry.videoUrl' () {
            this.releaseVideoBlobUrl();
            this.videoFailed = false;
            this.videoReadyToReveal = false;
            this.hasRequestedVideoLoad = false;
            if (this.isVideoVisible) {
                this.preparePlayableVideoUrl();
            }
        },
        shouldLoadVideo (value) {
            if (value) {
                this.preparePlayableVideoUrl();
            }
        }
    },
    computed: {
        isVisualCard () {
            return this.entry.postType === 'photo' || this.entry.postType === 'video';
        },
        isVideo () {
            return this.entry.postType === 'video';
        },
        typeLabel () {
            const labelMap = {
                photo: '照片',
                video: '视频'
            };

            return labelMap[this.entry.postType] || '动态';
        },
        visualImages () {
            return Array.isArray(this.entry.images)
                ? this.entry.images.filter(Boolean)
                : [];
        },
        videoThumbnail () {
            return this.entry.thumbnail || this.visualImages[0] || VIDEO_PLACEHOLDER;
        },
        videoPoster () {
            return '';
        },
        shouldShowVideoPosterOverlay () {
            return !this.videoReadyToReveal;
        },
        playableVideoUrl () {
            if (isQuickTimeCompatibilityUrl(this.entry.videoUrl) && !this.videoBlobUrl) {
                return '';
            }
            return this.videoBlobUrl || this.entry.videoUrl || '';
        },
        gridClass () {
            const imageCount = this.visualImages.length;

            if (imageCount === 1) {
                return 'single';
            }
            if (imageCount === 2) {
                return 'double';
            }
            if (imageCount === 4) {
                return 'quad';
            }
            return 'triple';
        },
        gridRenderKey () {
            return `${this.entry.id}-${this.entry.postType}-${this.entry.uploadStatus}-${this.visualImages.join('|')}`;
        },
        showUploadMeter () {
            return this.entry.uploadStatus === 'uploading' && Number(this.entry.uploadProgress?.totalAssets || 0) > 0;
        },
        entryUploadPercent () {
            return Math.max(0, Math.min(100, Number(this.entry.uploadProgress?.progressPercent || 0)));
        }
    }
};
</script>

<style lang="scss" scoped>
.album-card {
    position: relative;
    z-index: 1;
    padding: 14px;
    border-radius: 24px;
    background: #fff;
    box-shadow: 0 14px 34px rgba(39, 54, 82, 0.08);
}

.card-grid {
    display: grid;
    gap: 6px;
    margin-bottom: 12px;
}

.card-grid.single {
    grid-template-columns: 1fr;
}

.card-grid.double {
    grid-template-columns: repeat(2, 1fr);
}

.card-grid.quad {
    grid-template-columns: repeat(2, 1fr);
}

.card-grid.triple {
    grid-template-columns: repeat(3, 1fr);
}

.photo-button {
    position: relative;
    padding: 0;
    overflow: hidden;
    border: 0;
    border-radius: 16px;
    background:
        linear-gradient(110deg, rgba(236, 241, 245, 0.92) 8%, rgba(246, 249, 250, 0.98) 18%, rgba(236, 241, 245, 0.92) 33%);
    background-size: 220% 100%;
    aspect-ratio: 1 / 1;
    animation: media-skeleton 1.15s ease-in-out infinite;
}

.photo-button img,
.video-thumb {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.video-button {
    background: #eef3f7;
}

.album-image {
    opacity: 0;
    transform: scale(1.015);
    transition: opacity 0.22s ease, transform 0.32s ease;
}

.album-image.loaded {
    opacity: 1;
    transform: scale(1);
}

.video-poster-overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    transition: opacity 0.28s ease;
}

.video-poster-overlay.hidden {
    opacity: 0;
}

.video-thumb {
    background: #eef3f7;
}

.photo-button:has(.album-image.loaded),
.photo-button:has(.video-thumb) {
    animation: none;
}

.video-badge {
    position: absolute;
    right: 10px;
    bottom: 10px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border-radius: 999px;
    background: rgba(17, 20, 28, 0.72);
    color: #fff;
    font-size: 11px;
    line-height: 1;
    z-index: 2;
}

@keyframes media-skeleton {
    0% {
        background-position: 120% 0;
    }

    100% {
        background-position: -80% 0;
    }
}

.video-icon {
    font-size: 10px;
}

.card-meta {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding-top: 4px;
}

.meta-main {
    flex: 1;
    min-width: 0;
}

.meta-status {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
    color: #6f7585;
    font-size: 12px;
}

.type-tag {
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 10px;
    border-radius: 999px;
    background: rgba(5, 194, 203, 0.12);
    color: #058891;
    font-size: 12px;
    font-weight: 600;
}

.meta-author {
    color: #4f5566;
    font-weight: 600;
}

.meta-caption {
    margin-bottom: 6px;
    color: #202738;
    font-size: 15px;
    line-height: 1.45;
}

.meta-location {
    color: #949aa7;
    font-size: 12px;
}

.entry-upload-meter {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 10px;
}

.entry-progress-track {
    height: 6px;
    background: rgba(219, 138, 31, 0.12);
}

.entry-progress-bar {
    background: linear-gradient(90deg, #ffbe52, #f59634);
}

.entry-upload-text {
    color: #b57212;
    font-size: 12px;
    font-weight: 600;
}

.status-tag {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 999px;
    background: #edf6ff;
    color: #387ce8;
}

.status-tag.pending {
    background: #fff5e9;
    color: #db8a1f;
}

.meta-actions {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    position: relative;
    z-index: 6;
}

.action-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    height: 32px;
    padding: 0 10px;
    border: 0;
    border-radius: 16px;
    background: #f4f6fa;
    color: #566078;
    font-size: 12px;
    touch-action: manipulation;
}

.more-button {
    min-width: 32px;
    font-weight: 700;
    letter-spacing: 2px;
}

.floating-menu {
    position: absolute;
    right: 14px;
    bottom: 78px;
    z-index: 20;
    width: 128px;
    padding: 10px 12px;
    border-radius: 18px;
    background: rgba(19, 23, 33, 0.92);
    color: #fff;
    box-shadow: 0 16px 30px rgba(12, 15, 26, 0.2);
}

.floating-menu::after {
    content: '';
    position: absolute;
    right: 18px;
    bottom: -6px;
    width: 12px;
    height: 12px;
    background: rgba(19, 23, 33, 0.92);
    transform: rotate(45deg);
}

.menu-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 30px;
}

.menu-button {
    min-width: 48px;
    min-height: 34px;
    border: 0;
    background: transparent;
    color: inherit;
    font-size: 13px;
    touch-action: manipulation;
}

.danger-button {
    color: #ff8c8c;
}
</style>
